<template>
  <b-container fluid>
    <b-row class="d-flex">
      <b-col class="d-flex flex-grow-1">
        <backlink class="mt-20" />
        <div class="flex-grow-1">
          <h1>
            Transfert inter-entrepôts
            <div v-if="!isBusy" class="subtitle">#{{ transfer.transfer_number }}</div>
          </h1>
        </div>
      </b-col>
    </b-row>

    <div v-if="isBusy" class="loader"></div>
    <p v-if="isBusy" class="text-center">Les données sont en cours de chargement...</p>

    <b-row v-else>
      <b-col>
        <div class="bottom-actions scan-bar">
          <b-button v-if="inSaving" variant="outline-secondary" disabled>Enregistrement...</b-button>
          <template v-else>
            <!-- QR-004 : dépannage quand l'étiquette est illisible. La douchette reste le
                 mode nominal, sélectionné par défaut. Même présentation qu'au retour client. -->
            <ScanModeSwitch v-model="scanMode" class="scan-mode" />
            <!-- QR-006 : en mode photo, le viseur prend la place du champ. Il n'en occupe
                 que la hauteur utile, pour laisser voir la liste, et masquer le champ évite
                 de faire surgir le clavier du mobile sur un écran où l'on ne tape pas. -->
            <CameraScanner v-if="cameraEntry" @decoded="onCameraDecoded" @close="closeCameraScanner" />
            <b-button-group v-else>
              <input type="text" v-model="internal_code"
                :placeholder="manualEntry ? 'Saisir un code' : 'Scanner un code'"
                class="form-control" ref="internal_code" @keyup.enter="submitManualEntry()" />
              <b-button v-if="manualEntry" variant="outline-secondary" :disabled="!internal_code"
                @click="submitManualEntry()" style="border-left: 1px solid #e9e9e9">OK</b-button>
            </b-button-group>
          </template>
        </div>

        <!-- Provenance et destination se lisent en une ligne chacune : le libellé sert de
             repère, la valeur porte l'information. -->
        <dl class="transfer-origin">
          <div class="step">
            <dt>Provenance</dt>
            <dd>{{ transfer.source_warehouse ? transfer.source_warehouse.name : "—" }}</dd>
          </div>
          <div class="step">
            <dt>Destination</dt>
            <dd>{{ transfer.target_warehouse ? transfer.target_warehouse.name : "—" }}</dd>
          </div>
        </dl>
        <p v-if="transfer.comment" class="pre-line text-muted">{{ transfer.comment }}</p>

        <h2>
          <i class="far fa-dolly"></i>
          Matériel à réceptionner
        </h2>

        <div class="table-wrapper">
          <b-table ref="table" hover show-empty :fields="fields" :items="lines">
            <template slot="empty" slot-scope="scope">
              <div class="text-center mt-2">Ce transfert ne contient aucune ligne</div>
            </template>

            <template slot="label" slot-scope="data">
              {{ data.item.label }}
              <div class="text-muted">{{ data.item.internal_code }}</div>
            </template>
            <template slot="quantity" slot-scope="data">
              {{ scannedQuantity(data.item) }} / {{ data.item.remaining_quantity }}
            </template>
            <template slot="state" slot-scope="data">
              <b-badge v-if="isLineComplete(data.item)" variant="success">
                <i class="fa fa-check"></i>
                Réceptionné
              </b-badge>
              <b-badge v-else-if="scannedQuantity(data.item) > 0" variant="warning">
                <i class="fa fa-exclamation"></i>
                Partiel
              </b-badge>
              <b-badge v-else variant="secondary">En transit</b-badge>
            </template>
          </b-table>
        </div>
      </b-col>
    </b-row>

    <div v-if="!isBusy" class="bottom-actions">
      <b-button
        :variant="inSaving ? 'outline-secondary' : 'outline-primary'"
        :disabled="inSaving || totalScanned == 0"
        @click="validate()"
      >
        {{ inSaving ? "Enregistrement..." : "Valider la réception" }}
        <i v-if="!inSaving" class="fa fa-chevron-right"></i>
      </b-button>
    </div>

    <b-modal id="modal-partial" hide-footer centered no-close-on-backdrop>
      <template slot="modal-title">Réception incomplète</template>
      <div class="d-block text-center">
        <h3>
          {{ missingCount }} ligne(s) n'ont pas été scannées. Elles resteront « en transit » et le
          transfert restera ouvert.
        </h3>
      </div>
      <b-button class="mt-3" variant="success" block @click="send()">Confirmer la réception</b-button>
      <b-button
        class="mt-3"
        variant="outline-secondary"
        block
        @click="$root.$emit('bv::hide::modal', 'modal-partial')"
        >Annuler</b-button
      >
    </b-modal>
  </b-container>
</template>

<script>
import scannedValueMixin from "@/mixins/scannedValue";

export default {
  name: "WarehouseTransferMobileDetail",
  mixins: [scannedValueMixin],

  data() {
    return {
      fields: [
        {
          key: "label",
          label: "Matériel",
        },
        {
          key: "quantity",
          label: "Scanné",
        },
        {
          key: "state",
          label: "État",
        },
      ],
      transfer: {},
      isBusy: true,
      inSaving: false,
      internal_code: null,
      // Quantités scannées sur cette session, indexées par identifiant de ligne.
      scanned: {},
    };
  },
  computed: {
    /**
     * Lignes encore en transit (ce qui a déjà été réceptionné n'est plus à scanner).
     */
    lines() {
      return (this.transfer.lines || [])
        .filter((line) => line.remaining_quantity > 0)
        .map((line) => ({
          id: line.id,
          type: line.type,
          label: line.product_label,
          internal_code:
            line.type == "reference"
              ? line.reference && line.reference.internal_code
              : line.product && line.product.internal_code,
          remaining_quantity: line.remaining_quantity,
        }));
    },
    totalScanned() {
      return Object.keys(this.scanned).reduce((total, key) => total + this.scanned[key], 0);
    },
    missingCount() {
      return this.lines.filter((line) => !this.isLineComplete(line)).length;
    },
  },
  mounted() {
    this.load();
  },
  watch: {
    internal_code: function (internal_code) {
      // QR-004 : en saisie manuelle, la validation est explicite (bouton / Entrée).
      if (internal_code && !this.manualEntry) {
        // La douchette tape caractère par caractère : on attend la fin de la salve.
        this.handleScannedValueDebounced(internal_code);
      }
    },
  },
  methods: {
    load() {
      this.isBusy = true;
      this.$http
        .get(process.env.BACK_API_URL + "warehouse-transfer/" + this.$route.params.id)
        .then((response) => {
          this.transfer = response.data || {};
          // Le nom complet du produit est porté par la ligne quel que soit son type.
          (this.transfer.lines || []).forEach((line) => {
            line.product_label = line.product
              ? line.product.name
              : (line.reference && line.reference.product_name) || "Référence unique";
          });
          this.isBusy = false;
          // Le champ de scan n'est rendu qu'une fois le chargement terminé :
          // le focus ne peut être posé qu'après ce rendu.
          this.$nextTick(() => {
            if (this.$refs.internal_code) {
              this.$refs.internal_code.focus();
            }
          });
        })
        .catch(() => {
          this.$toastr.error(
            "La fiche demandée n'a pas été trouvée. Assurez-vous que l'URL saisie soit correcte."
          );
          this.isBusy = false;
        });
    },
    scannedQuantity(line) {
      return this.scanned[line.id] || 0;
    },
    isLineComplete(line) {
      return this.scannedQuantity(line) >= line.remaining_quantity;
    },
    /**
     * QR-003 : appelé par le mixin une fois la valeur normalisée.
     * Pointe le code scanné sur une ligne du transfert (référence unique ou produit à la vente).
     */
    onScanResolved(code) {
      const line = this.lines.find(
        (item) => item.internal_code && item.internal_code.toLowerCase() == code.toLowerCase()
      );

      if (!line) {
        this.$toastr.error("Le code " + code + " ne fait pas partie de ce transfert.");
        return;
      }
      if (this.isLineComplete(line)) {
        this.$toastr.warning("La quantité attendue pour " + code + " est déjà scannée.");
        return;
      }

      this.$set(this.scanned, line.id, this.scannedQuantity(line) + 1);
    },
    validate() {
      if (this.missingCount > 0) {
        this.$root.$emit("bv::show::modal", "modal-partial");
        return;
      }
      this.send();
    },
    send() {
      this.$root.$emit("bv::hide::modal", "modal-partial");
      this.inSaving = true;

      const lines = Object.keys(this.scanned)
        .filter((id) => this.scanned[id] > 0)
        .map((id) => ({ line_id: parseInt(id, 10), quantity: this.scanned[id] }));

      this.$http
        .patch(process.env.BACK_API_URL + "warehouse-transfer/" + this.$route.params.id + "/receive", {
          lines: lines,
        })
        .then(() => {
          this.$toastr.success("Réception enregistrée.");
          this.inSaving = false;
          this.$router.push({ name: "ReceptionMobileList" });
        })
        .catch((error) => {
          this.$toastr.error(
            (error.response && error.response.data && error.response.data.message) ||
              "Échec de la réception."
          );
          this.inSaving = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/_variables.scss";

// Zone de scan / saisie manuelle : même barre qu'au retour client
// (BusinessReturnMobileDetail), d'où la classe `bottom-actions` conservée pour en
// hériter tous les styles. Seul le positionnement est neutralisé : en mobile,
// `.bottom-actions` est collée en bas de l'écran, ce qui n'a pas de sens ici — la
// barre appartient au flux, au-dessus de la liste du matériel.
.bottom-actions.scan-bar {
  position: static;
  box-shadow: none;
  border: 1px solid #eeeeee;
  margin-bottom: 15px;
}

// Bandeau d'en-tête du transfert : d'où vient le matériel, où il est attendu. Le
// libellé est en retrait typographique (gris, petit, capitales) pour que le nom de
// l'entrepôt ressorte seul à la lecture, et la colonne de libellés est de largeur
// fixe pour que les deux valeurs s'alignent.
.transfer-origin {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid $border-10p;

  .step {
    display: flex;
    align-items: baseline;

    & + .step {
      margin-top: 8px;
    }
  }

  dt {
    flex: 0 0 100px;
    text-transform: uppercase;
    letter-spacing: 0.1rem;
    font-size: 10px;
    color: $text-50p;
    @extend .clanprobook;
  }

  dd {
    flex: 1;
    margin: 0;
    font-size: 13px;
    color: $text;
    @extend .clanprobold;
  }
}

.scan-bar {
  .btn-group {
    width: 100%;
  }

  input {
    border-radius: 0;
    border: none;
    padding: 5px;
    margin: 20px 25px;
    @extend .clanprobook;
  }

  // Le champ de scan porte déjà la marge basse : le sélecteur de mode se cale
  // sur son retrait horizontal sans en rajouter.
  .scan-mode {
    margin: 20px 25px 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ReceptionMobile/WarehouseTransferMobileDetail.vue